import * as React from 'react';
import { batch, shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import { IoMdArrowBack } from 'react-icons/io';

import { OscillometryDetailedHeader } from '../Components/OscillometryDetailedHeader';
import { OscillometryDetailedFullView } from './OscillometryDetailedFullView';
import { OscillometryDetailedTableView } from './OscillometryDetailedTableView';
import { LoaderContainer } from '../../../components/Utilities/LoaderContainer';
import { ModalBlankTemplate } from '../../../components/Modals/ModalBlankTemplate';

import { useAppSelector, useAppDispatch } from '../../../redux/store';
import { OrganizationType, OrganizationConsent, PatientDemographics } from '../../../Types/Common/Types';
import { OscillometryMeasurement, VersionlessRangeKey } from '../../../Types/Common/MeasurementTypes';
import { PatientID, UploadGroupID } from '../../../Types/Common/BrandedTypes';
import { OrgSelectOption } from '../../../Types/MiscTypes';
import { getParticipantMeasurements, updateMeasurementWithBody, updateOscillometryWaveform } from '../../../libs/trialsLib';
import { getDemographics } from '../../../libs/patientDemographicLib';
import { OverreadSubmission } from '../Modals/OscillometryOverreadModal';
import { getSignedUrlFromS3, getStudyID } from '../../../libs/zephyrxLib';
import { openFileInNewWindow } from '../../../libs/pdfLib';
import { getCurrentOrganization, ACCEPTABLE, USABLE, NOT_ACCEPTABLE } from '../../../libs/dataLib';
import { isEmptyObjectOrArray } from '../../../libs/utilsLib';
import { getOrganizationConsentForPatient, getOrganizationConsent, getActiveOrganizationIDsForPatient } from '../../../libs/organizationConsentLib';
import { getOrganizationsForProvider, getProviderFromOrganization } from '../../../libs/organizationProviderLib';
import { updateViewingAs } from '../../../redux/provider/actionCreators';
import { setSelectedOrg } from '../../../redux/organizationProvider/actionCreators';
import { setOrganizationConsent, setPatientName, setSpirometerID } from '../../../redux/patient/actionCreators';
import { getSpirometerFromPatientID } from '../../../libs/spirometerLib';
import { getPatient } from '../../../libs/patientLib';

import './OscillometryDetailedView.scss';

const { useState, useEffect, useMemo, useCallback, useRef } = React;

/**
 * Extract the versionless portion of a measurementID by stripping the version suffix (_V\d+).
 * e.g. "2026-01-20T21:52:51.000Z_TOSCWFM_V4" → "2026-01-20T21:52:51.000Z_TOSCWFM"
 */
const getVersionlessMeasurementID = (measurementID: string): string => {
  return measurementID.replace(/_V\s*\d+$/, '');
};

/**
 * Compare two measurementIDs by their versionless prefix.
 * Handles cases where the API returns a versioned ID that doesn't strictly equal the local one.
 */
const measurementIDsMatch = (a: string, b: string): boolean => {
  return getVersionlessMeasurementID(a) === getVersionlessMeasurementID(b);
};

export interface GroupedOscillometryMeasurement {
  testId: string;
  uploadGroupId: UploadGroupID | undefined;
  measurements: OscillometryMeasurement[];
  bestMeasurements: OscillometryMeasurement | undefined;
  waveforms: OscillometryMeasurement[];
  calibration: OscillometryMeasurement[];
}

interface OscillometryDetailedViewProps { };

export const OscillometryDetailedView: React.FC<OscillometryDetailedViewProps> = () => {
  const { id: participantID, measurementID } = useParams<{ id: PatientID; measurementID: VersionlessRangeKey }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const authenticatedProvider = useAppSelector(state => state.provider, shallowEqual);
  const isReadOnly = authenticatedProvider.viewingAs?.readOnly;

  const { detailedOrganizationsForProvider } = useAppSelector(state => state.organizationsForProvider, shallowEqual);
  const { detailedOrganizations, organizationsDropdown } = useAppSelector(state => state.organizations, shallowEqual);
  const { organizationsForProviderDropdown } = useAppSelector(state => state.organizationsForProvider, shallowEqual);
  const organizationList = authenticatedProvider.fullAdmin ? organizationsDropdown : organizationsForProviderDropdown;

  let organization: OrganizationType = useAppSelector(state =>
    authenticatedProvider.fullAdmin
      ? state.organizations.detailedOrganizations[authenticatedProvider.viewingAs.organizationID]
      : state.organizationsForProvider.detailedOrganizationsForProvider[authenticatedProvider.viewingAs.organizationID]
  );
  organization = getCurrentOrganization(
    authenticatedProvider.viewingAs,
    authenticatedProvider.fullAdmin ? detailedOrganizations : detailedOrganizationsForProvider
  );
  const program = useAppSelector(state => state.programs[organization?.program?.toUpperCase()]);

  // Guard: don't attempt auth checks or data fetching until redux is hydrated
  const lacksNecessaryDataToLoad = useMemo(() =>
    !organization
    || !authenticatedProvider
    || isEmptyObjectOrArray(organization)
    || isEmptyObjectOrArray(authenticatedProvider)
    || !authenticatedProvider.viewingAs?.organizationID
    || !program
    || isEmptyObjectOrArray(program),
    [authenticatedProvider, organization, program]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [oscillometryMeasurements, setOscillometryMeasurements] = useState<OscillometryMeasurement[]>([]);
  const [sessionDemographics, setSessionDemographics] = useState<PatientDemographics | undefined>(undefined);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isTableView, setIsTableView] = useState(false);

  // Prevent duplicate concurrent fetches
  const fetchInProgress = useRef(false);

  const toggleTableView = () => setIsTableView(!isTableView);

  // Fallback org consent for fullAdmins
  const getOrganizationConsentFallback = useCallback(async () => {
    if (!participantID) return null;
    if (authenticatedProvider.fullAdmin) console.log('invoking org consent fallback');
    const orgs = (await getOrganizationConsent(participantID)).consents
      .filter((org: OrganizationConsent) => org.revoked !== 1);
    return orgs.length
      ? await getOrganizationConsentForPatient(orgs[0].organizationID, participantID)
      : null;
  }, [authenticatedProvider, participantID]);

  // Group oscillometry measurements
  // Note: On this page, getParticipantMeasurements is called with a specific measurementID,
  // so all returned measurements belong to the same session. We find the target measurement
  // using versionless ID matching, then group by its testId within this scoped result set.
  // On pages that return multiple sessions, testId alone (e.g. "001") is NOT globally unique
  // and should not be used for cross-session grouping.
  const groupedMeasurement = useMemo((): GroupedOscillometryMeasurement | undefined => {
    if (!oscillometryMeasurements || isEmptyObjectOrArray(oscillometryMeasurements)) return undefined;

    const targetMeasurement = oscillometryMeasurements.find(m => {
      return measurementIDsMatch(m.measurementID, measurementID ?? '')
        || m.measurementID.startsWith(measurementID ?? '');
    });

    if (!targetMeasurement) {
      console.warn('No measurement found matching measurementID:', measurementID);
      return undefined;
    }

    const targetUploadGroupId = targetMeasurement?.uploadGroupId;
    const targetTestId = targetMeasurement.testId;
    const targetId = targetUploadGroupId ?? targetTestId;

    const measurements = oscillometryMeasurements.filter(m => {
      if (m.type.startsWith('OSCCAL')) return false;
      const idToUse = m?.uploadGroupId ?? m?.testId;
      return idToUse === targetId;
    });

    const waveforms = measurements
      .filter(m => m.type.startsWith('OSCWFM'))
      .sort((a, b) => {
        const timeA = a.roundtripTime ? new Date(a.roundtripTime).getTime() : 0;
        const timeB = b.roundtripTime ? new Date(b.roundtripTime).getTime() : 0;
        return timeB - timeA;
      });

    return {
      testId: targetTestId,
      uploadGroupId: targetUploadGroupId,
      measurements,
      bestMeasurements: measurements.find(m => m.type === 'OSCAVG'),
      waveforms,
      calibration: oscillometryMeasurements.filter(m => m.type.startsWith('OSCCAL'))
    };
  }, [oscillometryMeasurements, measurementID]);

  // Fetch oscillometry measurements on mount (with authorization checks)
  // Gated on lacksNecessaryDataToLoad so we wait for redux to be ready
  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!participantID || !measurementID) {
        setIsLoading(false);
        return;
      }

      // Wait for redux to hydrate before doing anything
      if (lacksNecessaryDataToLoad) return;

      // Prevent duplicate concurrent fetches
      if (fetchInProgress.current) return;
      fetchInProgress.current = true;

      setIsLoading(true);
      setUnauthorized(false);

      try {
        // --- Authorization checks ---
        const paramsFromURL = new URLSearchParams(window.location.search);
        const passedOrganizationID = paramsFromURL.get('organizationID');
        let updatedViewingAs = authenticatedProvider?.viewingAs;

        // Handle organizationID URL param to switch viewing context
        if (passedOrganizationID) {
          try {
            const providerOrganizationIDs = await getOrganizationsForProvider(authenticatedProvider.providerID);
            if (!isEmptyObjectOrArray(providerOrganizationIDs?.organizationIDs)) {
              const providerHasConsent = providerOrganizationIDs.organizationIDs.includes(passedOrganizationID);
              const providerNeedsViewingAsUpdate = passedOrganizationID !== authenticatedProvider.viewingAs?.organizationID;
              if (providerHasConsent && providerNeedsViewingAsUpdate) {
                const newViewingAsOrganization = detailedOrganizationsForProvider[passedOrganizationID];
                const providerPermissionForOrg = await getProviderFromOrganization(
                  newViewingAsOrganization.organizationID,
                  authenticatedProvider.providerID
                );
                updatedViewingAs = {
                  organizationID: newViewingAsOrganization.organizationID,
                  name: newViewingAsOrganization.name,
                  program: newViewingAsOrganization.program,
                  orgAdmin: providerPermissionForOrg?.orgAdmin,
                  readOnly: providerPermissionForOrg?.readOnly,
                };
                const selectedOrg = organizationList.find(
                  (org: OrgSelectOption) => org.id === newViewingAsOrganization.organizationID
                );
                if (selectedOrg) await dispatch(setSelectedOrg(selectedOrg));
                await dispatch(updateViewingAs(authenticatedProvider.providerID, updatedViewingAs));
              }
            }
          } catch (e) {
            console.error('Error getting organizationIDs for provider:', e);
            setIsLoading(false);
            fetchInProgress.current = false;
            return setUnauthorized(true);
          }
        }

        // Check patient is consented to the provider's org (skip for fullAdmin)
        if (!authenticatedProvider?.fullAdmin) {
          try {
            const { organizationIDs } = (await getActiveOrganizationIDsForPatient(participantID)) || [];
            if (!organizationIDs.includes(updatedViewingAs?.organizationID)) {
              console.log("PATIENT NOT A MEMBER OF PROVIDER'S CURRENT VIEWING ORG", organizationIDs, updatedViewingAs?.organizationID);
              setIsLoading(false);
              fetchInProgress.current = false;
              return setUnauthorized(true);
            }
          } catch (e) {
            console.error('ERROR GETTING ORGANIZATION IDs:', e);
            setIsLoading(false);
            fetchInProgress.current = false;
            return setUnauthorized(true);
          }

          // Fetch and store org consent in redux (needed by MeasurementTrialLabelsDropdown)
          try {
            const orgConsent = await getOrganizationConsentForPatient(updatedViewingAs?.organizationID, participantID);
            if (orgConsent && !isEmptyObjectOrArray(orgConsent)) {
              dispatch(setOrganizationConsent(orgConsent));
            }
          } catch (e) {
            console.error('Error fetching org consent for provider:', e);
          }
        }

        // For fullAdmins, verify org consent with fallback
        if (authenticatedProvider?.fullAdmin) {
          try {
            let orgConsent = await getOrganizationConsentForPatient(updatedViewingAs?.organizationID, participantID);
            if (!orgConsent || isEmptyObjectOrArray(orgConsent)) {
              orgConsent = await getOrganizationConsentFallback();
            }
            if (!orgConsent || isEmptyObjectOrArray(orgConsent)) {
              console.log('FULL ADMIN: No org consent found for participant');
              setIsLoading(false);
              fetchInProgress.current = false;
              return setUnauthorized(true);
            }
            // Store org consent in redux (needed by MeasurementTrialLabelsDropdown)
            dispatch(setOrganizationConsent(orgConsent));
          } catch (e) {
            console.error('Error checking org consent for fullAdmin:', e);
            // Allow fullAdmin to proceed even if consent check fails
          }
        }
        // --- End authorization checks ---

        try {
          const participant = await getPatient(participantID);
          const [currentPatientDemographics, patientStudyID, spirometerIDs] = await Promise.all([
            getDemographics(participantID, participant.demographicID),
            organization?.featureSet?.useStudyID ? getStudyID(program?.program, participantID) : { studyID: undefined },
            getSpirometerFromPatientID(participantID)
          ]);
          batch(() => {
            dispatch(setPatientName({
              name: patientStudyID.studyID ? organization.featureSet.nameStudyID ? patientStudyID.studyID : `${currentPatientDemographics?.name || ''} (${patientStudyID.studyID})` : currentPatientDemographics?.name || '',
              spirometer: spirometerIDs.spirometerIDs,
              studyID: patientStudyID?.studyID
            }));
            dispatch(setSpirometerID(spirometerIDs.spirometerIDs[0]));
          });
        } catch (e) {
          console.log('ERROR GETTING PATIENT DATA: ', e);
          setIsLoading(false);
          fetchInProgress.current = false;
          return setUnauthorized(true);
        }

        const measurements = await getParticipantMeasurements(participantID, measurementID, true);
        setOscillometryMeasurements(measurements);

        // Fetch demographics if available
        const avgMeasurement = measurements.find((m: OscillometryMeasurement) => m.type === 'OSCAVG');

        if (avgMeasurement?.demographicID) {
          try {
            const demographics = await getDemographics(participantID, avgMeasurement.demographicID);
            setSessionDemographics(demographics);
          } catch (e) {
            console.error('Error fetching demographics:', e);
          }
        }
      } catch (e) {
        console.error('Error fetching oscillometry measurements:', e);
        setErrorMessage(translationText.errorFetchingData || 'Error fetching measurement data');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
        fetchInProgress.current = false;
      }
    };

    fetchMeasurements();
  }, [participantID, measurementID, translationText, authenticatedProvider, dispatch, organizationList, detailedOrganizationsForProvider, getOrganizationConsentFallback, lacksNecessaryDataToLoad, program, organization]);

  // Calculate acceptability status
  const getAcceptabilityStatus = useCallback((
    group: GroupedOscillometryMeasurement | undefined,
    demographics: PatientDemographics | undefined
  ): string => {
    if (group?.bestMeasurements?.acceptability) return group?.bestMeasurements?.acceptability;
    if (!group?.bestMeasurements?.R5?.cv) return NOT_ACCEPTABLE;

    const r5CoV = group.bestMeasurements.R5.cv;
    const acceptableManoeuvres = group.waveforms.filter(w => w.rejected !== undefined && !w.rejected).length;

    let isChild = false;
    if (demographics?.birth) {
      const birthDate = new Date(parseInt(demographics.birth));
      const today = new Date();
      const ageInYears = (today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      isChild = ageInYears < 12;
    }

    const repeatabilityThreshold = isChild ? 15 : 10;
    const usableThreshold = 15;

    if (acceptableManoeuvres < 3) {
      return NOT_ACCEPTABLE;
    }

    if (r5CoV <= repeatabilityThreshold) {
      return ACCEPTABLE;
    } else if (r5CoV > repeatabilityThreshold && r5CoV <= usableThreshold) {
      return isChild ? NOT_ACCEPTABLE : USABLE;
    } else {
      return NOT_ACCEPTABLE;
    }
  }, []);

  // Calculate repeatability status
  const getRepeatabilityStatus = useCallback((
    group: GroupedOscillometryMeasurement | undefined,
    demographics: PatientDemographics | undefined
  ): string => {
    if (group?.bestMeasurements?.repeatable !== undefined) return group.bestMeasurements.repeatable ? 'Yes' : 'No';
    if (!group?.bestMeasurements?.R5?.cv) return 'No';

    const r5CoV = group.bestMeasurements.R5.cv;

    let isChild = false;
    if (demographics?.birth) {
      const birthDate = new Date(parseInt(demographics.birth));
      const today = new Date();
      const ageInYears = (today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      isChild = ageInYears < 12;
    }

    const repeatabilityThreshold = isChild ? 15 : 10;
    return r5CoV <= repeatabilityThreshold ? 'Yes' : 'No';
  }, []);

  // Download PDF handler
  const handleClickGeneratePdf = async () => {
    if (!groupedMeasurement || !participantID) return;

    const groupEnvironment = groupedMeasurement.bestMeasurements?.sourceEnvironment
      ? groupedMeasurement.bestMeasurements?.sourceEnvironment
      : process.env.REACT_APP_STAGE === 'preview'
        ? 'prod'
        : process.env.REACT_APP_STAGE;

    try {
      const pdfPath = `processed/${participantID}/${groupedMeasurement.testId}/report.pdf`;
      console.log(pdfPath)
      const signedURL = await getSignedUrlFromS3(pdfPath, 'getObject', `gdt-ingestion-${groupEnvironment}`);
      const response = await fetch(signedURL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const blob = await response.blob();
      openFileInNewWindow(new Blob([blob], { type: 'application/pdf' }));
    } catch (e: any) {
      console.error('Error downloading PDF:', e.message);
      setErrorMessage(translationText.errorDownloadingPdf || 'Error downloading PDF report');
      setShowErrorModal(true);
    }
  };

  // Navigate back handler
  const handleNavigateBack = () => {
    navigate(`/?participantID=${participantID}`);
  };

  // Update local measurement (for labels dropdown)
  // const updateLocalMeasurement = (updatedMeasurement: OscillometryMeasurement) => {
  //   setOscillometryMeasurements(prev =>
  //     prev.map(m => measurementIDsMatch(m.measurementID, updatedMeasurement.measurementID) ? { ...m, ...updatedMeasurement } : m)
  //   );
  // };

  const refetchMeasurements = useCallback(async () => {
    if (!participantID || !measurementID) return;

    try {
      const measurements = await getParticipantMeasurements(participantID, measurementID, true);
      setOscillometryMeasurements(measurements);

      const avgMeasurement = measurements.find((m: OscillometryMeasurement) => m.type === 'OSCAVG');
      if (avgMeasurement?.demographicID) {
        try {
          const demographics = await getDemographics(participantID, avgMeasurement.demographicID);
          setSessionDemographics(demographics);
        } catch (e) {
          console.error('Error re-fetching demographics:', e);
        }
      }
    } catch (e) {
      console.error('Error re-fetching measurements after update:', e);
    }
  }, [participantID, measurementID]);

  // Toggle rejected status on a waveform measurement
  // Calls the API, then re-fetches all measurements since the backend recalculates OSCAVG and session quality
  const toggleRejected = useCallback(async (waveform: OscillometryMeasurement) => {
    if (isReadOnly || !participantID || !measurementID || !groupedMeasurement?.bestMeasurements) return;

    try {
      await updateOscillometryWaveform({
        patientID: participantID,
        measurementID: waveform.measurementID,
        averageMeasurementID: groupedMeasurement.bestMeasurements.measurementID,
        rejected: !waveform.rejected,
      });

      // Re-fetch all measurements since backend recalculates OSCAVG, session quality, etc.
      const measurements = await getParticipantMeasurements(participantID, measurementID, true);
      setOscillometryMeasurements(measurements);

      // Re-fetch demographics if needed (OSCAVG may have changed)
      const avgMeasurement = measurements.find((m: OscillometryMeasurement) => m.type === 'OSCAVG');
      if (avgMeasurement?.demographicID) {
        try {
          const demographics = await getDemographics(participantID, avgMeasurement.demographicID);
          setSessionDemographics(demographics);
        } catch (e) {
          console.error('Error re-fetching demographics:', e);
        }
      }
    } catch (e) {
      console.error('Error toggling rejected status:', e);
      setErrorMessage(translationText.errorUpdatingMeasurement || 'Error updating measurement');
      setShowErrorModal(true);
    }
  }, [isReadOnly, participantID, measurementID, groupedMeasurement, translationText]);

  // Save acceptability criteria for a waveform measurement
  // Sends all criteria fields along with the waveform and average measurementIDs, then re-fetches
  const saveAcceptabilityCriteria = useCallback(async (
    waveform: OscillometryMeasurement,
    criteria: Partial<Record<string, boolean>>
  ) => {
    if (isReadOnly || !participantID || !measurementID || !groupedMeasurement?.bestMeasurements) return;

    // If any editable criterion is false, reject the waveform
    const hasFailedCriteria = Object.values(criteria).some(v => v === false);

    try {
      await updateOscillometryWaveform({
        patientID: participantID,
        measurementID: waveform.measurementID,
        averageMeasurementID: groupedMeasurement.bestMeasurements.measurementID,
        ...criteria,
        rejected: hasFailedCriteria,
      });

      // Re-fetch all measurements since backend may recalculate based on criteria changes
      const measurements = await getParticipantMeasurements(participantID, measurementID, true);
      setOscillometryMeasurements(measurements);

      // Re-fetch demographics if needed
      const avgMeasurement = measurements.find((m: OscillometryMeasurement) => m.type === 'OSCAVG');
      if (avgMeasurement?.demographicID) {
        try {
          const demographics = await getDemographics(participantID, avgMeasurement.demographicID);
          setSessionDemographics(demographics);
        } catch (e) {
          console.error('Error re-fetching demographics:', e);
        }
      }
    } catch (e) {
      console.error('Error saving acceptability criteria:', e);
      setErrorMessage(translationText.errorUpdatingMeasurement || 'Error updating measurement');
      setShowErrorModal(true);
      throw e;
    }
  }, [isReadOnly, participantID, measurementID, groupedMeasurement, translationText]);

  /**
    * Save session-level overread (Accept / Borderline Accept / Reject) on the OSCAVG measurement.
    * Sends provider identity info alongside the QA decision, then re-fetches all measurements.
    */
  const saveOverread = useCallback(async (overread: OverreadSubmission) => {
    if (isReadOnly || !participantID || !groupedMeasurement?.bestMeasurements) return;

    try {
      await updateMeasurementWithBody({
        patientID: participantID,
        measurementID: groupedMeasurement.bestMeasurements.measurementID,
        overread: {
          acceptability: overread.acceptability,
          overreadingOrganizationID: authenticatedProvider?.viewingAs?.organizationID,
          providerID: authenticatedProvider?.providerID,
          providerName: `${authenticatedProvider?.name}`.trim(),
          timestamp: Date.now(),
          ...(overread.betweenManeuverStatement ? { betweenManeuverStatement: overread.betweenManeuverStatement } : {}),
          ...(overread.notes ? { notes: overread.notes } : { notes: '' }),
        },
      });

      // Re-fetch all measurements to get updated OSCAVG with overread
      const measurements = await getParticipantMeasurements(participantID, measurementID, true);
      setOscillometryMeasurements(measurements);
    } catch (e) {
      console.error('Error saving overread:', e);
      setErrorMessage(translationText.errorUpdatingMeasurement || 'Error updating measurement');
      setShowErrorModal(true);
      throw e;
    }
  }, [isReadOnly, participantID, measurementID, groupedMeasurement, authenticatedProvider, translationText]);

  // Unauthorized state
  if (unauthorized) {
    return (
      <Container className="OscillometryDetailedView">
        <Button variant="link" className="btn-back pl-0" onClick={handleNavigateBack}>
          <IoMdArrowBack size={30} />{" "}{translationText.backToParticipant || 'Back to Participant'}
        </Button>
        <p className="text-center my-5">
          {translationText.unauthorized || 'You are not authorized to view this page'}
        </p>
      </Container>
    );
  }

  // Loading state (covers both redux hydration and data fetching)
  if (isLoading || lacksNecessaryDataToLoad) {
    console.log('isloading: ', isLoading)
    console.log('lacksNecessaryData: ', lacksNecessaryDataToLoad)
    return <LoaderContainer />;
  }

  // No data state
  if (!groupedMeasurement || !groupedMeasurement.bestMeasurements) {
    return (
      <Container className="OscillometryDetailedView">
        <Container className="emptyStatsTable">
          <span>{translationText.noMeasurementsFound || 'No Measurements Found'}</span>
        </Container>
      </Container>
    );
  }

  const acceptabilityStatus = getAcceptabilityStatus(groupedMeasurement, sessionDemographics);

  return (
    <Container className="DetailedStatsComponent OscillometryDetailedView">
      <Button variant="link" className="btn-back pl-0" onClick={handleNavigateBack}><IoMdArrowBack size={30} />{" "}{translationText.backToParticipant || 'Back to Participant'}</Button>
      <OscillometryDetailedHeader
        groupedMeasurement={groupedMeasurement}
        isTableView={isTableView}
        toggleTableView={toggleTableView}
        handleClickGeneratePdf={handleClickGeneratePdf}
        saveOverread={saveOverread}
        isReadOnly={isReadOnly}
      />

      {isTableView ? (
        <OscillometryDetailedTableView
          groupedMeasurement={groupedMeasurement}
          toggleRejected={toggleRejected}
          isReadOnly={isReadOnly}
          program={program}
        />
      ) : (
        <OscillometryDetailedFullView
          groupedMeasurement={groupedMeasurement}
          sessionDemographics={sessionDemographics}
          acceptabilityStatus={acceptabilityStatus}
          getRepeatabilityStatus={() => getRepeatabilityStatus(groupedMeasurement, sessionDemographics)}
          refetchMeasurements={refetchMeasurements}
          toggleRejected={toggleRejected}
          saveAcceptabilityCriteria={saveAcceptabilityCriteria}
          organization={organization}
          program={program}
          isReadOnly={isReadOnly}
          participantID={participantID}
        />
      )}

      {/* Error Modal */}
      <ModalBlankTemplate
        visibleModal={showErrorModal}
        setVisibleModal={setShowErrorModal as React.Dispatch<React.SetStateAction<string | boolean>>}
        title={translationText.error || 'Something went wrong'}
        body={errorMessage || translationText.refreshAndTryAgain || 'Please refresh your page and try again'}
        center
      />
    </Container>
  );
};

export default OscillometryDetailedView;